/**
 * Report a tab the browser killed while it was open, on the next page load.
 *
 * WHY. A tab killed for running out of memory runs no more JavaScript, so
 * nothing in it can report the crash. iOS Safari is the case that matters:
 * home went white after about a dozen load-mores, and error_logs had nothing.
 * Chrome can send its own crash report (pages/api/errors/browser-report);
 * Safari cannot, so this infers it.
 *
 * HOW. While a page is open it writes a heartbeat to localStorage every few
 * seconds: where it is, whether it is on screen, and what it is holding (rows,
 * images, DOM size, heap where the browser says). `pagehide` marks it closed
 * cleanly. On the next load, a heartbeat that is recent, was on screen, and
 * was never closed cleanly belonged to a tab that died in front of the reader.
 * iOS reloads a crashed tab by itself, so that next load is usually seconds
 * later, which is also why the window is short.
 *
 * NOT A CRASH:
 *  - a tab iOS discards in the background: it went hidden first, and a hidden
 *    heartbeat is never reported;
 *  - another tab's heartbeat: on prod those were closed tabs, never crashes;
 *  - this tab going on to a different page, or Back to the same one: the
 *    reader left, even when the clean mark never arrived (`isCrashReload`);
 *  - an ordinary reload, close or navigation away: `pagehide` marked it clean;
 *  - our own code sending the page elsewhere: `markTabLeaving` marked it clean.
 *
 * NOT OURS TO SAY: a crash in a Chromium browser, which reports it itself with
 * the heartbeat attached (`sendsOwnCrashReport`).
 *
 * localStorage, not sessionStorage: it is not certain sessionStorage survives
 * the content process dying, and the record has to.
 *
 * OPEN QUESTION. WebKit fires no `pagehide` for a document whose `load` event
 * never fired, and a product page with a dozen ad iframes can sit unloaded for
 * minutes. Leaving such a page looks exactly like a crash here. So every
 * heartbeat carries `ready_state`, and every report carries how the next load
 * arrived (`next_path`, `nav_type`): a crash reloads the same page, a reader
 * who left opens a different one.
 *
 * ANSWERED 2026-10-06. Rows with that context split cleanly: pages that had
 * finished loading held 7-10k DOM nodes when they died, while the unloaded ones
 * held 214-1055, and one of those was a product page left for "/" two seconds
 * after it opened. So this tab's own heartbeat from a page that never finished
 * loading, followed by a load of a different path, is the reader leaving and is
 * not reported. The same path reloaded still is: that is what a crash looks like
 * -- unless the page never finished loading and was open under five seconds,
 * which no crash this sentinel can read looks like (see `diedOnArrival`).
 */

import { trackError, ErrorSeverity } from './error-tracking';
import { TAB_BEAT_COOKIE, encodeTabBeat, decodeTabBeat } from './tab-crash-cookie';
import { TAB_ENDED_MESSAGE } from './tab-ended-message';

const KEY = 'sw_tab_heartbeats';
const TAB_KEY = 'sw_tab_id';

/** How often an open, visible page writes its heartbeat. */
export const BEAT_MS = 5000;
/**
 * Extra heartbeats in a page's first seconds, before the steady beat starts.
 *
 * WHY. Prod 2026-10-09..23, Chrome out-of-memory reports on Android: 12 of the
 * 41 that carried a heartbeat had only the first one -- open 0 s, 266 DOM nodes,
 * 7 images, no iframes, 32 MB heap. They died inside five seconds, and the only
 * beat they left was taken before anything had loaded, so it could not say what
 * grew. A beat each second until the steady one does.
 */
export const EARLY_BEATS_MS = [1000, 2000, 3000, 4000];
/** A heartbeat older than this at the next load is a tab long gone, not a crash. */
export const CRASH_WINDOW_MS = 30000;
/** Heartbeats are pruned after this, crash or not. */
const KEEP_MS = 10 * 60 * 1000;

export interface Heartbeat {
  tab: string;
  path: string;
  ts: number;
  visible: boolean;
  clean: boolean;
  /** Seconds since this page view started. */
  up: number;
  /**
   * The deployment this page's code came from. The report is sent by the next
   * load, which may already be a newer build, so without this a crash on a tab
   * opened before a deploy reads as the new build crashing.
   */
  build?: string;
  stats: Record<string, number | string>;
}

type Beats = Record<string, Heartbeat>;

/** Counters the page adds to every heartbeat (load-mores so far, and so on). */
const context: Record<string, number | string> = {};

/** Set by `markTabLeaving`: every heartbeat from here on is a clean one. */
let leaving = false;

/**
 * Say this page is leaving on purpose, just before code sends it elsewhere with
 * `location.href`.
 *
 * WHY. Prod 2026-10-06: three Chrome rows from a product page that had loaded
 * (410-426 DOM nodes, 43-49 MB heap), open 1 s, then "/" in the same tab 1 s
 * later. That is the product page's own redirect home when the listing does not
 * load, not a crash, but its clean mark from `pagehide` never reached the next
 * load. Marking clean before the navigation starts does not depend on it.
 */
export function markTabLeaving(): void {
  leaving = true;
  const beats = read();
  const me = tabId();
  if (!beats[me]) return;
  beats[me] = { ...beats[me], clean: true, ts: Date.now() };
  write(beats);
}

/** Add or bump a value carried on every heartbeat until the next page view. */
export function noteCrashContext(key: string, value: number | string): void {
  context[key] = value;
}

/** A new page view starts its counters over. */
export function resetCrashContext(): void {
  for (const key of Object.keys(context)) delete context[key];
}

export function bumpCrashContext(key: string): void {
  context[key] = (typeof context[key] === 'number' ? (context[key] as number) : 0) + 1;
}

function read(): Beats {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? (parsed as Beats) : {};
    // eslint-disable-next-line sellwild-code/catch-must-log -- storage blocked (Safari private mode) or corrupt: the sentinel simply does not run
  } catch {
    return {};
  }
}

function write(beats: Beats): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(beats));
    // eslint-disable-next-line sellwild-code/catch-must-log -- storage blocked or full: a missed heartbeat only means a crash goes unreported
  } catch {
    /* blocked */
  }
}

/**
 * The same heartbeat, in a cookie Chrome's crash report carries to
 * pages/api/errors/browser-report (see utils/tab-crash-cookie). Ten minutes:
 * the report is sent within a second or two of the crash.
 */
function writeBeatCookie(beat: Heartbeat): void {
  try {
    const value = encodeTabBeat({
      ...beat.stats,
      tab: beat.tab,
      clean: String(beat.clean),
      path: beat.path,
      up: beat.up,
      ts: beat.ts,
      build: beat.build,
    });
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${TAB_BEAT_COOKIE}=${value}; Path=/; Max-Age=600; SameSite=Lax${secure}`;
    // eslint-disable-next-line sellwild-code/catch-must-log -- cookies blocked: the crash report simply arrives without the heartbeat
  } catch {
    /* blocked */
  }
}

/**
 * What the heartbeat cookie says about this tab's last page, set beside the
 * localStorage record: 'clean', 'unclean', 'none' (no cookie, or one from
 * another tab or page) or 'older' (written before the stored heartbeat).
 *
 * WHY. Prod 2026-10-29: an iPhone product page, loaded, 1116 DOM nodes and
 * 2.9 MP of images, open 3 s, then the same page reloaded 1 s later. Nothing
 * holding that little runs out of memory; it is a reader reloading whose clean
 * mark from `pagehide` did not reach localStorage, the same miss as desktop
 * Chromium (`sendsOwnCrashReport`), but Safari sends no crash report to lean on.
 * `pagehide` writes the cookie too, and a tab that really died never ran it,
 * so a clean cookie newer than the stored heartbeat is a page that left.
 */
export function cookieMark(beat: Heartbeat): 'clean' | 'unclean' | 'none' | 'older' {
  try {
    const raw = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${TAB_BEAT_COOKIE}=`))
      ?.slice(TAB_BEAT_COOKIE.length + 1);
    const fields = decodeTabBeat(raw);
    if (!fields || fields.tab !== beat.tab || fields.path !== beat.path || !fields.clean) return 'none';
    if (Number(fields.ts) < beat.ts) return 'older';
    return fields.clean === 'true' ? 'clean' : 'unclean';
    // eslint-disable-next-line sellwild-code/catch-must-log -- cookies blocked: the report says none and goes on as before
  } catch {
    return 'none';
  }
}

function tabId(): string {
  try {
    const existing = sessionStorage.getItem(TAB_KEY);
    if (existing) return existing;
    const id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem(TAB_KEY, id);
    return id;
    // eslint-disable-next-line sellwild-code/catch-must-log -- no sessionStorage: a fresh id per page view still works
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }
}

/**
 * Megapixels of the images this page has loaded, to one decimal.
 *
 * WHY. Prod 2026-10-09..23: the pages that died after loading held 60-115
 * images and 15-18 iframes on a 4 GB phone with a 33 MB JS heap. The heap is
 * not it, and an image count cannot tell 100 thumbnails from 10 full-size
 * photos. A decoded image costs about 4 bytes a pixel, so this is the number
 * that says whether the photos are what filled the tab.
 */
function decodedImageMegapixels(): number {
  let pixels = 0;
  for (const img of Array.from(document.images)) {
    if (img.complete) pixels += img.naturalWidth * img.naturalHeight;
  }
  return Math.round(pixels / 100000) / 10;
}

/**
 * The last script or iframe added to the page, and how many so far.
 *
 * WHY. Prod 2026-10-24, the first Chrome out-of-memory row after the early
 * beats shipped: a weatherbug reader on Android 10, 4 GB, a beat at 0 s (266
 * DOM nodes, 7 images, no iframes, 32 MB heap), dead 2 s later with no 1 s
 * beat. A timer that never fires means the main thread never came free, and
 * that is the thread an ad creative in a same-process iframe runs on too, so no
 * beat can say what took it. A MutationObserver can: its callback runs as soon
 * as the task that inserted the element ends, so the cookie names the last
 * script or frame that arrived before the page went quiet.
 */
let added = 0;
let lastAdded: { el: string; ms: number } | null = null;

/**
 * Where an element with no src and no id was put: the nearest ancestor with an
 * id (an ad slot's div names its slot) or the nearest `<sellwild-ad>`, else the
 * parent's tag. `#__next` is skipped, since everything React renders is in it.
 *
 * WHY. Prod 2026-10-24/25, the first two Chrome out-of-memory rows that named
 * a last element: `iframe#inline` and `script#inline`. Both are what ad code
 * inserts, and neither said whose.
 */
function placeOf(el: Element): string {
  const parent = el.parentElement;
  if (!parent) return 'detached';
  const owner = parent.closest('[id]:not(#__next), sellwild-ad');
  if (!owner) return parent.tagName.toLowerCase();
  return owner.id ? `#${owner.id}` : owner.tagName.toLowerCase();
}

/**
 * `iframe:securepubads.g.doubleclick.net/pagead/ads` for an element with a src
 * (host and path, never the query, which carries ids), `iframe#<id>` for one
 * without (ad frames are often about:blank, and their id names the slot), and
 * `script#inline@<place>` for one with neither (see `placeOf`).
 * Null for anything that is not a script or an iframe.
 */
export function describeAddedElement(node: Node): string | null {
  const tag = node.nodeName.toLowerCase();
  if (tag !== 'script' && tag !== 'iframe') return null;
  const el = node as Element;
  const src = el.getAttribute('src');
  if (src) {
    try {
      const url = new URL(src, location.href);
      return `${tag}:${url.host}${url.pathname}`.slice(0, 120);
      // eslint-disable-next-line sellwild-code/catch-must-log -- a src the URL parser refuses still says which kind of element came last
    } catch {
      return `${tag}:unparsed`;
    }
  }
  const name = el.id || el.getAttribute('name');
  if (name) return `${tag}#${name}`.slice(0, 120);
  return `${tag}#inline@${placeOf(el)}`.slice(0, 120);
}

/**
 * The page's iframes that are not GAM's own, by host, most first:
 * `ads.pubmatic.com:4,blank:3,www.youtube.com:1`. A frame with no src, or an
 * about:blank one, counts as `blank`.
 *
 * WHY. Prod 2026-10-09..25: the product pages that ran out of memory after
 * loading held 13-17 iframes with under 3 decoded megapixels of photos and a
 * 33 MB JS heap. The memory is in the frames, and a count cannot say whose
 * they are, so it cannot say which ad partner to cut on a 4 GB phone.
 */
export function frameHosts(): string {
  const counts = new Map<string, number>();
  for (const frame of Array.from(document.getElementsByTagName('iframe'))) {
    if (frame.id.startsWith('google_ads_iframe')) continue;
    const src = frame.getAttribute('src');
    let host = 'blank';
    if (src && !src.startsWith('about:')) {
      try {
        host = new URL(src, location.href).host || 'blank';
        // eslint-disable-next-line sellwild-code/catch-must-log -- a src the URL parser refuses is still a frame to count
      } catch {
        host = 'unparsed';
      }
    }
    counts.set(host, (counts.get(host) ?? 0) + 1);
  }
  return Array.from(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([host, n]) => `${host}:${n}`)
    .join(',');
}

function addedStats(): Record<string, number | string> {
  if (!lastAdded) return { added };
  return { added, last_el: lastAdded.el, last_el_ms: lastAdded.ms };
}

/**
 * The page's long animation frames (over 50 ms) so far: how many, the longest,
 * whose script ran longest in it, and when the main thread last came free.
 *
 * WHY. Prod 2026-10-27 10:53Z: a weatherbug reader on Android 10, 4 GB, a beat
 * at 0 s (668 DOM nodes, 47 images, 32 MB heap, no iframes), no 1 s beat, dead
 * under 2 s later, and `added=0`: no script or iframe arrived before the page
 * went quiet. What held the thread was code already on the page, which the
 * MutationObserver cannot see. Chrome's Long Animation Frame entries name the
 * script (URL and function) that ran longest in each frame, buffered from the
 * page's start, so the cookie says whose code held the thread up to the moment
 * it was last free. No entry and no 1 s beat means one task never ended.
 */
interface ScriptTiming {
  duration: number;
  invoker?: string;
  invokerType?: string;
  sourceURL?: string;
  sourceFunctionName?: string;
}
type LongFrame = PerformanceEntry & { scripts?: ScriptTiming[] };

let longFrames = 0;
let longest: { ms: number; src: string; at: number } | null = null;
let longEnd = 0;

/**
 * `sellwild.com/_next/static/chunks/pages/_app-1a2b.js:onLoad` (host, path and
 * function, never the query), the invoker when there is no URL, and `none` for
 * a frame with no script in it (the time went to style, layout or paint).
 *
 * A script with a URL and no function name is named by how it was called
 * instead (`Window.setTimeout`, `DOMWindow.onmessage`), or by its invoker type
 * (`classic-script`) when the invoker is itself a URL, whose query carries ids.
 * Prod 2026-10-27 22:10Z: the first row with long frames said only
 * `sellwild.com/product/105081047` -- code the page ran with no file of its own
 * (an inline script, or ad code's eval), and nothing about what started it.
 */
export function describeLongFrameScript(script: ScriptTiming | undefined): string {
  if (!script) return 'none';
  let where = '';
  if (script.sourceURL) {
    try {
      const url = new URL(script.sourceURL, location.href);
      where = `${url.host}${url.pathname}`;
      // eslint-disable-next-line sellwild-code/catch-must-log -- a URL the parser refuses still says a script ran
    } catch {
      where = 'unparsed';
    }
  }
  const invoker = script.invoker && !script.invoker.includes('://') ? script.invoker : script.invokerType;
  const fn = script.sourceFunctionName || invoker || '';
  return [where, fn].filter(Boolean).join(':').slice(0, 160) || 'unknown';
}

function noteLongFrame(entry: LongFrame): void {
  longFrames += 1;
  longEnd = Math.max(longEnd, Math.round(entry.startTime + entry.duration));
  if (longest && entry.duration <= longest.ms) return;
  const top = (entry.scripts ?? []).reduce<ScriptTiming | undefined>(
    (best, s) => (!best || s.duration > best.duration ? s : best),
    undefined
  );
  longest = { ms: Math.round(entry.duration), src: describeLongFrameScript(top), at: Math.round(entry.startTime) };
}

function longFrameStats(): Record<string, number | string> {
  if (!longest) return { long_n: longFrames };
  return { long_n: longFrames, long_ms: longest.ms, long_src: longest.src, long_at: longest.at, long_end: longEnd };
}

/**
 * The last step an ad took through GAM, and whose ad it was:
 * `ad_last=render:div-gpt-ad-product-1`, `ad_creative=adv:5012/li:6893/cr:13844`,
 * `ad_ms` (when, since the sentinel started) and `ad_n` (steps so far). The
 * steps are `req` (sent), `resp` (an answer came back), `render` (the creative
 * went into its frame), `empty` (nothing to show) and `load` (its frame
 * finished loading).
 *
 * WHY. Prod 2026-10-27 22:10Z, Chrome on Android 10, 4 GB, a partner-widget
 * reader on a product page: the 5 s beat at 10.1 s page time held 1067 DOM
 * nodes, 5.9 MP of photos, a 33 MB heap and 2 iframes with no GAM frame yet;
 * the last long frame had ended at 8.4 s; the ad tag's script went in at about
 * 8 s. No 10 s beat ever came and the tab died out of memory at about 18.8 s.
 * So one task that started after the ads were asked for never ended, and a
 * creative running in a frame on the page's own thread is the likeliest
 * owner. Each GAM event rewrites the cookie as it happens, so a tab that dies
 * with `ad_last=render:...` and no `load` names the creative that was drawing,
 * and its ids are what GAM blocks it by.
 */
interface GamSlot {
  getSlotElementId?: () => string;
  getResponseInformation?: () => GamCreative | null;
}
interface GamCreative {
  advertiserId?: number | null;
  lineItemId?: number | null;
  creativeId?: number | null;
}
type GamEvent = GamCreative & { slot?: GamSlot; isEmpty?: boolean };

let adSteps = 0;
let lastAd: { step: string; creative: string; ms: number } | null = null;

/** `adv:5012/li:6893/cr:13844`, leaving out what GAM did not say; `` for none. */
export function describeAdCreative(info: GamCreative | null | undefined): string {
  if (!info) return '';
  const parts: string[] = [];
  if (info.advertiserId) parts.push(`adv:${info.advertiserId}`);
  if (info.lineItemId) parts.push(`li:${info.lineItemId}`);
  if (info.creativeId) parts.push(`cr:${info.creativeId}`);
  return parts.join('/');
}

function noteAdStep(step: string, event: GamEvent | undefined, ms: number): void {
  const slot = event?.slot;
  let id = 'unknown';
  let info: GamCreative | null | undefined = event?.creativeId || event?.lineItemId ? event : null;
  try {
    id = slot?.getSlotElementId?.() || 'unknown';
    info = info ?? slot?.getResponseInformation?.();
    // eslint-disable-next-line sellwild-code/catch-must-log -- a slot GPT has already destroyed still says a step happened
  } catch {
    /* destroyed slot */
  }
  adSteps += 1;
  lastAd = { step: `${step}:${id}`.slice(0, 120), creative: describeAdCreative(info), ms };
}

function adStats(): Record<string, number | string> {
  if (!lastAd) return { ad_n: adSteps };
  return { ad_n: adSteps, ad_last: lastAd.step, ad_ms: lastAd.ms, ...(lastAd.creative ? { ad_creative: lastAd.creative } : {}) };
}

/** What changes between beats: the last element added, the long frames, the last ad step. */
function betweenBeatStats(): Record<string, number | string> {
  return { ...addedStats(), ...longFrameStats(), ...adStats() };
}

/**
 * Follow GAM's slot events, queued on `googletag.cmd` like the widget's own
 * slot scripts (and components/ad-refresh-guard), so it is safe before GPT
 * has loaded. `onStep` runs after each one is noted.
 */
function watchAdSteps(startedAt: number, isLive: () => boolean, onStep: () => void): void {
  const w = window as unknown as {
    googletag?: { cmd?: unknown[]; pubads?: () => { addEventListener?: (e: string, h: (ev: GamEvent) => void) => void } };
  };
  w.googletag = w.googletag || {};
  w.googletag.cmd = w.googletag.cmd || [];
  const listen = (step: (ev: GamEvent) => string) => (ev: GamEvent) => {
    if (!isLive()) return;
    noteAdStep(step(ev), ev, Date.now() - startedAt);
    onStep();
  };
  (w.googletag.cmd as (() => void)[]).push(() => {
    const pubads = w.googletag?.pubads?.();
    if (!pubads?.addEventListener) return;
    pubads.addEventListener('slotRequested', listen(() => 'req'));
    pubads.addEventListener('slotResponseReceived', listen(() => 'resp'));
    pubads.addEventListener('slotRenderEnded', listen((ev) => (ev.isEmpty ? 'empty' : 'render')));
    pubads.addEventListener('slotOnload', listen(() => 'load'));
  });
}

/** What the page is holding right now. Cheap enough to run every few seconds. */
export function memoryStats(): Record<string, number | string> {
  const stats: Record<string, number | string> = {
    dom_nodes: document.getElementsByTagName('*').length,
    images: document.images.length,
    iframes: document.getElementsByTagName('iframe').length,
    gam_frames: document.querySelectorAll('iframe[id^="google_ads_iframe"]').length,
    frame_hosts: frameHosts(),
    sliders: document.getElementsByClassName('listing-slider').length,
    rows: document.getElementsByClassName('showcase-heading-row').length,
    videos: document.getElementsByTagName('video').length,
    image_mp: decodedImageMegapixels(),
  };
  const heap = (performance as Performance & { memory?: { usedJSHeapSize?: number } }).memory?.usedJSHeapSize;
  if (typeof heap === 'number') stats.js_heap_mb = Math.round(heap / 1048576);
  const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  if (typeof deviceMemory === 'number') stats.device_memory_gb = deviceMemory;
  return stats;
}

/** The heartbeats that look like a tab that died on screen, as of `now`. */
export function suspectedCrashes(beats: Beats, now: number): Heartbeat[] {
  return Object.values(beats).filter(
    (b) => b && !b.clean && b.visible && now - b.ts >= 0 && now - b.ts < CRASH_WINDOW_MS
  );
}

/**
 * True when this load is what a crashed tab does next: the SAME tab opens the
 * SAME page again, by a reload or a fresh navigation, never by Back.
 *
 * WHY. Prod 2026-10-05..13, 109 sentinel rows checked against Chrome's own
 * crash reports (pages/api/errors/browser-report), which Chrome sends for every
 * renderer it loses:
 *  - 40 desktop rows where the same tab went on to a different page (one was
 *    the owner leaving /dash/inbox for "/" after 29 minutes): Chrome reported
 *    none of them. The clean mark from `pagehide` simply did not reach the next
 *    load in time.
 *  - 37 rows found by another tab: Chrome confirmed none, even on Android where
 *    it reports. A closed tab, not a dead one.
 *  - Same tab, same page: 8 of the 11 Android rows had a Chrome report beside
 *    them. That is the crash pattern; iOS reloads a dead tab in place too.
 * Back to the same page is the reader returning from elsewhere, not a reload.
 */
export function isCrashReload(beat: Heartbeat, me: string, nextPath: string, navType: string): boolean {
  return beat.tab === me && beat.path === nextPath && navType !== 'back_forward';
}

/**
 * True when the heartbeat came from a page that never finished loading and was
 * open under one steady beat (`BEAT_MS`): it never reached `complete`.
 *
 * WHY. Prod 2026-10-06, after the different-path rule above: two more rows,
 * both product pages still on the skeleton (214 DOM nodes, 7 images, 30 MB
 * heap), open 0 s, then the same listing again 2-3 s later -- an iOS reload in
 * the same tab, and an Android Chrome load in another. A page that has held
 * nothing for under five seconds has not run out of memory, and with no
 * `pagehide` before load it cannot be told from a reader who reloaded or
 * reopened a slow page. Whatever path comes next, and whichever tab, it is
 * not a crash this sentinel can say anything about.
 */
export function diedOnArrival(beat: Heartbeat): boolean {
  const readyState = beat.stats.ready_state;
  return typeof readyState === 'string' && readyState !== 'complete' && beat.up * 1000 < BEAT_MS;
}

/**
 * True for a Chromium browser that sends its own crash report for a renderer it
 * loses (pages/api/errors/browser-report): Chrome, Edge, Samsung Internet and
 * Electron, on a computer or on Android. Not Chrome on iOS (WebKit underneath)
 * and not an Android WebView inside another app, which sends no report.
 *
 * WHY. Prod 2026-10-28: two rows from one desktop Electron window on /sell, 8 GB,
 * 60-92 MB heap, 954-1094 DOM nodes, the page fully loaded, then the same page
 * again in the same tab with a heartbeat under a second old. That is the owner
 * hitting reload, whose clean mark did not arrive, not a tab out of memory. On
 * prod no desktop sentinel row was ever confirmed by a crash report (see
 * `isCrashReload`), so the sentinel adds only false rows there.
 *
 * Prod 2026-10-30 23:56Z: Samsung Internet on Android 10, a weatherbug product
 * page, and the browser's own "Tab crashed: out of memory" report 16 s earlier
 * for the same session, carrying the same heartbeat from the cookie and more of
 * it (the last ad creative, a later long frame). In 14 days every Android
 * Chromium sentinel row had that report beside it. Where the browser says so
 * itself, the sentinel's inference is the same crash counted twice.
 */
export function sendsOwnCrashReport(userAgent: string): boolean {
  return /Chrome\//.test(userAgent) && !/CriOS|iPhone|iPad|; wv\)/.test(userAgent);
}

/** How this document was reached: navigate, reload, back_forward, or unknown. */
function navigationType(): string {
  try {
    const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return entry?.type ?? 'unknown';
    // eslint-disable-next-line sellwild-code/catch-must-log -- no Navigation Timing in this browser: the report says unknown
  } catch {
    return 'unknown';
  }
}

function report(beat: Heartbeat, now: number, cookie: string): void {
  trackError(TAB_ENDED_MESSAGE, ErrorSeverity.ERROR, {
    component: 'tab-crash-sentinel',
    action: 'tab_crash',
    extra: {
      crashed_path: beat.path,
      next_path: location.pathname,
      nav_type: navigationType(),
      seconds_before_reload: Math.round((now - beat.ts) / 1000),
      seconds_open: beat.up,
      same_tab: String(beat.tab === tabId()),
      crashed_deployment: beat.build ?? 'unknown',
      // What the cookie copy of the heartbeat said (see `cookieMark`).
      cookie_mark: cookie,
      ...beat.stats,
    },
  });
}

let started = false;
/** Which start the live sentinel is, so an earlier start's GAM listeners stay quiet. */
let runs = 0;

/**
 * Pages the sentinel leaves alone: the admin dash. It is there for shoppers'
 * phones running out of memory; admins on a desktop gain nothing from it, and
 * a beat every 5 s (a count of every element, a localStorage write and a
 * cookie) is one more thing running on a page that is open for hours.
 */
export function isQuietPath(path: string): boolean {
  return path === '/dash' || path.startsWith('/dash/');
}

/**
 * Start the sentinel for this document. Idempotent; call once from `_app`.
 * Returns a stop function for tests.
 */
export function startTabCrashSentinel(): () => void {
  if (typeof window === 'undefined' || started) return () => undefined;
  started = true;

  const me = tabId();
  const startedAt = Date.now();

  // 1. Did this tab die on screen just before this load? Only this tab's own
  // record can say: another tab's unclean heartbeat is, on the prod evidence,
  // a tab that was closed (see `isCrashReload`). Those are left to expire.
  const now = Date.now();
  const nav = navigationType();
  const beats = read();
  const own = beats[me];
  if (own && suspectedCrashes({ [me]: own }, now).length > 0) {
    const mark = cookieMark(own);
    if (
      isCrashReload(own, me, location.pathname, nav) &&
      !diedOnArrival(own) &&
      !sendsOwnCrashReport(navigator.userAgent) &&
      mark !== 'clean'
    ) {
      report(own, now, mark);
    }
    delete beats[me];
    write(beats);
  }

  // 2. Keep our own heartbeat, except on the dash. A tab that moves onto the
  // dash drops its record once, so the dash can never read as a crash, and
  // picks the heartbeat up again when it leaves.
  let quiet = false;
  let lastBeat: Heartbeat | null = null;
  const beat = (clean = false) => {
    if (isQuietPath(location.pathname)) {
      lastBeat = null;
      if (!quiet) {
        const beats = read();
        if (beats[me]) {
          delete beats[me];
          write(beats);
        }
        quiet = true;
      }
      return;
    }
    quiet = false;
    const t = Date.now();
    const beats = read();
    for (const [id, b] of Object.entries(beats)) {
      if (!b || t - b.ts > KEEP_MS) delete beats[id];
    }
    beats[me] = {
      tab: me,
      path: location.pathname,
      ts: t,
      visible: document.visibilityState === 'visible',
      clean: clean || leaving,
      up: Math.round((t - startedAt) / 1000),
      build: process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID || undefined,
      // ready_state: a page still 'loading' or 'interactive' gets no pagehide in WebKit.
      // nav_ms: this beat in page time, to line up with the long frames' times.
      stats: { ...memoryStats(), ready_state: document.readyState, nav_ms: Math.round(performance.now()), ...betweenBeatStats(), ...context },
    };
    write(beats);
    writeBeatCookie(beats[me]);
    lastBeat = beats[me];
  };

  // 3. Between beats, each script or iframe added rewrites the cookie with the
  // last beat plus what just arrived (see `lastAdded`).
  const rewriteCookie = () => {
    if (lastBeat) writeBeatCookie({ ...lastBeat, stats: { ...lastBeat.stats, ...betweenBeatStats() } });
  };
  const observer =
    typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver((records) => {
          let found: string | null = null;
          for (const record of records) {
            for (const node of Array.from(record.addedNodes)) {
              const el = describeAddedElement(node);
              if (el) {
                added += 1;
                found = el;
              }
            }
          }
          if (!found) return;
          lastAdded = { el: found, ms: Date.now() - startedAt };
          rewriteCookie();
        });
  observer?.observe(document.documentElement, { childList: true, subtree: true });

  // 4. Each long frame that ends rewrites the cookie too (see `longFrameStats`).
  let longObserver: PerformanceObserver | null = null;
  if (typeof PerformanceObserver !== 'undefined' && PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame')) {
    longObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) noteLongFrame(entry as LongFrame);
      rewriteCookie();
    });
    longObserver.observe({ type: 'long-animation-frame', buffered: true });
  }

  // 5. So does each step an ad takes through GAM (see `lastAd`). GPT has no
  // way to take a listener back, so a stopped sentinel's listeners go quiet.
  const run = ++runs;
  watchAdSteps(startedAt, () => started && run === runs, rewriteCookie);

  beat();
  const beatIfVisible = () => {
    if (document.visibilityState === 'visible') beat();
  };
  const early = EARLY_BEATS_MS.map((ms) => setTimeout(beatIfVisible, ms));
  const interval = setInterval(beatIfVisible, BEAT_MS);
  const onVisibility = () => beat();
  const onHide = () => beat(true);
  // Restored from the back-forward cache: open again, so no longer clean.
  const onShow = (e: PageTransitionEvent) => {
    if (e.persisted) {
      leaving = false;
      beat();
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onHide);
  window.addEventListener('pageshow', onShow);

  return () => {
    early.forEach(clearTimeout);
    clearInterval(interval);
    observer?.disconnect();
    longObserver?.disconnect();
    added = 0;
    lastAdded = null;
    longFrames = 0;
    longest = null;
    longEnd = 0;
    adSteps = 0;
    lastAd = null;
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onHide);
    window.removeEventListener('pageshow', onShow);
    started = false;
    leaving = false;
  };
}
